import { useState } from 'react';
import {
    FormControl,
    FormLabel,
    Box,
    Stack,
    Flex,
    Text,
    Icon,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { LuLock } from 'react-icons/lu';
import { Checkbox, Skeleton } from '@/components/v4';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    dropDownFormStyle,
} from 'components/forms/new/formStyles';
import { useCompanyForm } from '../../hooks/useCompanyForm';
import { FORM_FIELD_PATHS, GLOBAL_OPTION } from '../../constants';

const DataResidencyRegion = ({
    darkMode,
    dataResidencyLocations = [],
    isLoadingDataResidencyLocations,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const {
        formData,
        updateFormData,
        initialFormData,
        residencyConfirmed,
        setResidencyConfirmed,
    } = useCompanyForm();

    const locationID = formData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
    const isLocked =
        !!initialFormData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
    const hasPendingSelection = !isLocked && !!locationID;

    const selectedOption = locationID
        ? dataResidencyLocations.find((loc) => loc.id === locationID)
        : null;

    const handleChange = (items) => {
        const realItems = (items || []).filter((item) => item.id);
        const newest = realItems.length
            ? realItems[realItems.length - 1]
            : null;
        updateFormData(
            FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID,
            newest?.id ?? null,
        );
        setResidencyConfirmed(false);
    };

    return (
        <FormControl
            sx={{ margin: '0 !important', paddingBottom: 2 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <FormLabel sx={formLabelStyling(darkMode)}>
                    Data Residency Region
                </FormLabel>
                <Box flex={1} maxWidth="400px" pt="15px">
                    {isLocked ? (
                        <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            minHeight="30px"
                            px={2}
                            borderRadius="6px"
                            border="1px solid"
                            borderColor={
                                darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                            }
                            bg={darkMode ? 'dark.bgDark' : 'gray.50'}
                            _hover={{
                                cursor: 'not-allowed',
                            }}>
                            {selectedOption ? (
                                <Box
                                    as="span"
                                    bg="gray.600"
                                    color="white"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    borderRadius="4px"
                                    px={2}
                                    py="2px">
                                    {selectedOption.name}
                                </Box>
                            ) : (
                                <Skeleton
                                    data-testid="residency-skeleton"
                                    height="18px"
                                    width="80px"
                                    borderRadius="4px"
                                />
                            )}
                            <Icon
                                as={LuLock}
                                color={darkMode ? 'gray.300' : 'gray.500'}
                                boxSize={3.5}
                            />
                        </Flex>
                    ) : (
                        <>
                            <Select
                                isMulti
                                selectedOptionColor="brand"
                                value={
                                    selectedOption
                                        ? [selectedOption]
                                        : [GLOBAL_OPTION]
                                }
                                options={dataResidencyLocations}
                                getOptionLabel={(item) => item.name}
                                getOptionValue={(item) => item.id}
                                isClearable={!!selectedOption}
                                isLoading={isLoadingDataResidencyLocations}
                                placeholder="Select data residency region"
                                closeMenuOnSelect={true}
                                backspaceRemovesValue={false}
                                tagVariant="solid"
                                menuPortalTarget={document.body}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                chakraStyles={dropDownFormStyle(darkMode, true)}
                                components={
                                    selectedOption
                                        ? undefined
                                        : { MultiValueRemove: () => null }
                                }
                                onChange={handleChange}
                            />
                            {hasPendingSelection && (
                                <Box mt={2}>
                                    <Text
                                        color="red.500"
                                        fontWeight="semibold"
                                        fontSize="sm">
                                        Once a Data Residency Region has been
                                        set, it cannot be changed.
                                    </Text>
                                    <Checkbox
                                        mt={1}
                                        colorScheme="red"
                                        isChecked={residencyConfirmed}
                                        onChange={(e) =>
                                            setResidencyConfirmed(
                                                e.target.checked,
                                            )
                                        }>
                                        <Text
                                            color="red.500"
                                            fontWeight="bold"
                                            fontSize="sm">
                                            I understand
                                        </Text>
                                    </Checkbox>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="The data residency region for this company. This can only be set once."
                        ariaLabel="data-residency-region-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default DataResidencyRegion;
